import { useState, useEffect } from "react";
import "./CRM.css"; // Reuse common styles
import "./Warehouse.css";
import dataService from "../services/dataService";
import Modal from "../components/Modal";
import { useToast } from "../context/ToastContext";
import { Search, Package, AlertTriangle, FileEdit } from "lucide-react";

function WarehouseScreen() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Main Warehouse - dubai");
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [reserveDescription, setReserveDescription] = useState("");
  const { addToast } = useToast();

  useEffect(() => {
    const fetchWarehouseData = async () => {
      try {
        const [prodRes, orderRes] = await Promise.all([
          dataService.getProducts(),
          dataService.getOrders()
        ]);
        setProducts(prodRes.data.data);
        setOrders(orderRes.data.data);
      } catch (error) {
        console.error("Failed to fetch warehouse data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouseData();
  }, []);

  const handleEditReserve = (product) => {
    setEditingProduct(product);
    setReserveDescription(product.reserveDescription || "");
    setIsEditModalOpen(true);
  };

  const handleSaveReserve = async () => {
    try {
      await dataService.updateProduct(editingProduct._id, {
        reserveDescription: reserveDescription
      });
      // Update local state
      setProducts(products.map(p =>
        p._id === editingProduct._id ? { ...p, reserveDescription: reserveDescription } : p
      ));
      setIsEditModalOpen(false);
      addToast("Reservation description updated", "success");
    } catch (err) {
      addToast("Failed to update reservation description", "error");
    }
  };

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Warehouse Inventory</h1>
          <p>Global stock management and location tracking</p>
        </div>
        <div className="header-actions" style={{ display: 'flex', gap: '12px' }}>
          <button className={`tab-btn ${activeTab === "Main Warehouse - dubai" ? "active" : ""}`} onClick={() => setActiveTab("Main Warehouse - dubai")}>Main Warehouse - dubai</button>
          <button className={`tab-btn ${activeTab === "West Coast Hub - sharjah" ? "active" : ""}`} onClick={() => setActiveTab("West Coast Hub - sharjah")}>West Coast Hub - sharjah</button>
        </div>
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-icon-small green">
            <Package size={18} />
          </div>
          <div className="stat-info">
            <span className="stat-label">Total Items</span>
            <h2 className="stat-value">
              {products
                .filter(p => p.warehouseLocation === activeTab)
                .reduce((acc, p) => acc + p.stockCount, 0)}
            </h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-small orange">
            <AlertTriangle size={18} />
          </div>
          <div className="stat-info">
            <span className="stat-label">Low Stock</span>
            <h2 className="stat-value">
              {products
                .filter(p => p.warehouseLocation === activeTab && p.stockCount <= p.minimumStock)
                .length}
            </h2>
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls">
          <h2 className="font-bold">Inventory at {activeTab}</h2>
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search inventory by name, SKU, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <p style={{ padding: '20px' }}>Loading inventory...</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Item Details</th>
                  <th>Current Stock</th>
                  <th>Reserved</th>
                  <th>Available</th>
                  <th>Reserved For</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {products
                  .filter(p =>
                    p.warehouseLocation === activeTab &&
                    (p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      p.sku.toLowerCase().includes(searchTerm.toLowerCase()))
                  )
                  .map((product) => {
                    const reservedQty = orders
                      .filter(o => o.warehouse === activeTab && ['Pending', 'Confirmed', 'Processing'].includes(o.status))
                      .reduce((acc, o) => {
                        const item = o.items.find(i => i.product === product.name);
                        return acc + (item ? item.quantity : 0);
                      }, 0);

                    return (
                      <tr key={product._id}>
                        <td>
                          <div className="font-bold">{product.name}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>SKU: {product.sku}</div>
                        </td>
                        <td>{product.stockCount}</td>
                        <td style={{ color: reservedQty > 0 ? '#3B82F6' : 'inherit' }}>{reservedQty}</td>
                        <td><span className="font-bold">{product.stockCount - reservedQty}</span></td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', color: '#4B5563' }}>
                              {product.reserveDescription || "N/A"}
                            </span>
                            <button
                              onClick={() => handleEditReserve(product)}
                              style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                              title="Edit Reservation"
                            >
                              <FileEdit size={14} color="#6B7280" />
                            </button>
                          </div>
                        </td>
                        <td>
                          <span className={`status-pill ${product.status.replace(/\s+/g, '-').toLowerCase()}`}>
                            {product.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                {products.length === 0 && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>No inventory found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Reservation Description"
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
            <button className="primary-btn" onClick={handleSaveReserve}>Save Description</button>
          </>
        }
      >
        <div className="modal-form">
          <div className="form-group">
            <label>Reserved For (Customer/Order Details)</label>
            <textarea
              value={reserveDescription}
              onChange={(e) => setReserveDescription(e.target.value)}
              placeholder="Enter who this stock is reserved for..."
              rows={4}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #D1D5DB' }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default WarehouseScreen;